import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetKYCInvestorInstanceRes } from '@dealstack/shared/lib/generated';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';

interface Props {
	investors: GetKYCInvestorInstanceRes[];
}

export const useInvestorNavigation = ({ investors }: Props) => {
	const navigate = useNavigate();
	const { investorId, dealId, mipId } = useRequiredParams('dealId', 'mipId', 'investorId');
	const [searchParams] = useSearchParams();

	const currentInvestorIndex = investors.findIndex((investor) => investor.id === investorId);

	const isFirstInvestor = currentInvestorIndex === 0;
	const isLastInvestor = currentInvestorIndex === investors.length - 1;
	const totalInvestors = investors.length;

	const nextInvestor = useMemo(() => {
		if (isLastInvestor || currentInvestorIndex === -1) {
			return undefined;
		}
		return investors[currentInvestorIndex + 1];
	}, [investors, currentInvestorIndex, isLastInvestor]);

	const previousInvestor = useMemo(() => {
		if (isFirstInvestor || currentInvestorIndex === -1) {
			return undefined;
		}
		return investors[currentInvestorIndex - 1];
	}, [investors, currentInvestorIndex, isFirstInvestor]);

	const navigateToInvestor = (targetInvestorId: string) => {
		const kycPageUrl = mipManagePath(`${MIP_MANAGE_ROUTES.KYC_PAGE}/main/:investorId`, {
			mipId,
			dealId,
			investorId: targetInvestorId,
		});
		navigate({ pathname: kycPageUrl, search: searchParams.toString() });
	};

	const goToNextInvestor = () => {
		if (nextInvestor) {
			navigateToInvestor(nextInvestor.id);
		}
	};

	const goToPreviousInvestor = () => {
		if (previousInvestor) {
			navigateToInvestor(previousInvestor.id);
		}
	};

	return {
		currentInvestorIndex,
		isFirstInvestor,
		isLastInvestor,
		totalInvestors,
		goToNextInvestor,
		goToPreviousInvestor,
	};
};
